import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const CONTACT_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ContactPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/contact#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL}/contact`,
      "name": "Contact DVS Power Washing | Free Quote Dallas TX",
      "description": "Contact DVS Power Washing for a free pressure washing estimate in Dallas Metroplex, TX. Call (214) 636-8343 or fill out our online form. Fast response guaranteed.",
      "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL}/#website` },
      "about": { "@id": `${import.meta.env.VITE_SITE_URL}/#business` },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Contact", "item": `${import.meta.env.VITE_SITE_URL}/contact` }
        ]
      }
    },
    {
      "@type": "LocalBusiness",
      "@id": `${import.meta.env.VITE_SITE_URL}/#business`,
      "name": "DVS Power Washing",
      "url": import.meta.env.VITE_SITE_URL,
      "telephone": "+12146368343",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Dallas",
        "addressRegion": "TX",
        "addressCountry": "US"
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
          "opens": "07:00",
          "closes": "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "08:00",
          "closes": "16:00"
        }
      ],
      "sameAs": [
        "https://www.instagram.com/dvs_power_washing_dallas/"
      ]
    }
  ]
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Canonical tag
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://dvspowerwashingdallas.com/contact';

    // Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contact';
    script.text = JSON.stringify(CONTACT_SCHEMA);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-contact');
      if (el) el.remove();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.service) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('address', formData.address);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d66h7ps8e72clamqhg6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          address: '',
          service: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      <section className="relative h-[450px] flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E3A5D] to-[#1A5280]"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Let's Get Your Property<br />Looking Brand New
          </h1>
          <p className="text-xl lg:text-2xl text-[#4FB3D9]">
            Free estimates. Fast response. Professional service.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-[#0E3A5D] mb-8">Get In Touch</h2>

              <a
                href="tel:2146368343"
                className="flex items-center space-x-3 mb-8 group"
              >
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-phone-fill text-4xl text-[#4FB3D9]"></i>
                </div>
                <span className="text-2xl font-bold text-gray-800 group-hover:text-[#0E3A5D] transition-colors">
                  (214) 636-8343
                </span>
              </a>

              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-3">Business Hours</h3>
                <div className="space-y-2 text-gray-600">
                  <p>Monday - Friday: 7:00 AM - 6:00 PM</p>
                  <p>Saturday: 8:00 AM - 4:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-3">Service Area</h3>
                <p className="text-gray-600">
                  Proudly serving Dallas Metroplex and the greater Dallas area
                </p>
              </div>

              <div className="flex items-center space-x-4 mb-8">
                <div className="bg-[#0E3A5D] text-white px-4 py-2 rounded-lg text-sm font-semibold">
                  <i className="ri-star-fill mr-1"></i>
                  5.0 Rated
                </div>
                <div className="bg-[#0E3A5D] text-white px-4 py-2 rounded-lg text-sm font-semibold">
                  <i className="ri-team-fill mr-1"></i>
                  Family Minority Owned
                </div>
              </div>

              <a
                href="https://www.instagram.com/dvs_power_washing_dallas/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 bg-gradient-to-r from-[#f09433] via-[#e6683c] via-[#dc2743] via-[#cc2366] to-[#bc1888] text-white px-6 py-3 rounded-xl font-bold text-base hover:opacity-90 transition-all shadow-md cursor-pointer whitespace-nowrap w-fit"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-instagram-fill text-xl"></i>
                </div>
                <span>Follow on Instagram</span>
              </a>
            </div>

            <div className="lg:col-span-3">
              <form
                id="contact_form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl shadow-xl p-10"
              >
                <div className="mb-5">
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full h-14 px-4 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#0E3A5D] focus:border-transparent"
                    placeholder="Your full name"
                  />
                </div>

                <div className="mb-5">
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full h-14 px-4 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#0E3A5D] focus:border-transparent"
                    placeholder="Your phone number"
                  />
                </div>

                <div className="mb-5">
                  <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full h-14 px-4 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#0E3A5D] focus:border-transparent"
                    placeholder="Property address"
                  />
                </div>

                <div className="mb-5">
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Needed *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full h-14 px-4 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#0E3A5D] focus:border-transparent"
                  >
                    <option value="">Select a service</option>
                    <option value="House Washing">House Washing</option>
                    <option value="Driveway & Sidewalk Cleaning">Driveway &amp; Sidewalk Cleaning</option>
                    <option value="Roof Soft Washing">Roof Soft Washing</option>
                    <option value="Fence & Patio Cleaning">Fence &amp; Patio Cleaning</option>
                    <option value="Commercial Pressure Washing">Commercial Pressure Washing</option>
                    <option value="Multiple Services">Multiple Services</option>
                    <option value="Not Sure">Not Sure</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message (Max 500 characters)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#0E3A5D] focus:border-transparent resize-none"
                    placeholder="Tell us about your project..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                    <i className="ri-checkbox-circle-fill mr-2"></i>
                    Thank you! We'll contact you shortly.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                    <i className="ri-error-warning-fill mr-2"></i>
                    Something went wrong. Please try again or call us directly.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0E3A5D] text-white h-14 rounded-lg font-bold text-lg hover:bg-[#0A2D47] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Request Free Quote'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-3">
                <i className="ri-shield-check-fill text-4xl text-[#0E3A5D]"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Licensed &amp; Insured</h3>
            </div>

            <div>
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-3">
                <i className="ri-team-fill text-4xl text-[#0E3A5D]"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Family Minority Owned</h3>
            </div>

            <div>
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-3">
                <i className="ri-star-fill text-4xl text-[#FFB800]"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800">5.0 Star Rated</h3>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0E3A5D]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-2xl lg:text-3xl text-white italic font-bold mb-5">
            "We don't cut corners — we clean them."
          </p>
          <p className="text-lg text-[#4FB3D9]">DVS Power Washing</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
